import React from 'react';
import { Plus, Loader2 } from 'lucide-react';
import LeadCard from './LeadCard';

/**
 * PipelineColumn - Kanban column for a pipeline stage
 * @param {Object} stage - The stage configuration
 * @param {Array} leads - Leads in this stage
 * @param {Array} allStages - All pipeline stages
 * @param {Function} onMoveStage - Callback to move lead to different stage
 * @param {Function} onScheduleTour - Callback to schedule tour
 * @param {Function} onSendEmail - Callback to send email
 * @param {Function} onViewDetails - Callback to view lead details
 * @param {Function} onQuickAdd - Callback to add new lead to this stage
 * @param {Boolean} isLoading - Loading state
 */
const PipelineColumn = ({ 
  stage, 
  leads, 
  allStages,
  onMoveStage, 
  onScheduleTour, 
  onSendEmail, 
  onViewDetails,
  onQuickAdd,
  isLoading
}) => {
  // Get stage color classes
  const getStageStyles = () => {
    const styles = {
      'gray': {
        header: 'bg-gray-100 border-gray-200',
        badge: 'bg-gray-200 text-gray-700',
        accent: 'border-t-4 border-gray-400'
      },
      'blue': {
        header: 'bg-blue-50 border-blue-100',
        badge: 'bg-blue-100 text-blue-700',
        accent: 'border-t-4 border-blue-400'
      },
      'yellow': {
        header: 'bg-yellow-50 border-yellow-100',
        badge: 'bg-yellow-100 text-yellow-700',
        accent: 'border-t-4 border-yellow-400'
      },
      'purple': {
        header: 'bg-purple-50 border-purple-100',
        badge: 'bg-purple-100 text-purple-700',
        accent: 'border-t-4 border-purple-400'
      },
      'orange': {
        header: 'bg-orange-50 border-orange-100',
        badge: 'bg-orange-100 text-orange-700',
        accent: 'border-t-4 border-orange-400'
      },
      'green': {
        header: 'bg-green-50 border-green-100',
        badge: 'bg-green-100 text-green-700',
        accent: 'border-t-4 border-green-400'
      },
      'red': {
        header: 'bg-red-50 border-red-100',
        badge: 'bg-red-100 text-red-700',
        accent: 'border-t-4 border-red-400'
      }
    };
    return styles[stage?.color] || styles.gray;
  };

  const styles = getStageStyles();

  return (
    <div className={`flex flex-col h-full min-w-[300px] max-w-[340px] bg-white rounded-xl shadow-sm border border-gray-200 ${styles.accent}`}>
      {/* Column Header */}
      <div className={`p-4 border-b rounded-t-xl ${styles.header}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{stage?.label || stage?.name}</h3>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
            {leads?.length || 0}
          </span>
        </div>
        
        {stage?.description && (
          <p className="text-xs text-gray-500">{stage.description}</p>
        )}
      </div>

      {/* Column Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px] max-h-[calc(100vh-280px)]">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-purple-600" size={24} />
          </div>
        ) : leads?.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p className="text-sm">No leads</p>
            <button
              onClick={() => onQuickAdd(stage)}
              className="mt-2 px-3 py-2 min-h-[44px] text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg font-medium"
            >
              + Add one
            </button>
          </div>
        ) : (
          leads.map(lead => (
            <LeadCard
              key={lead.id}
              lead={lead}
              stage={stage}
              allStages={allStages}
              onMoveStage={onMoveStage}
              onScheduleTour={onScheduleTour}
              onSendEmail={onSendEmail}
              onViewDetails={onViewDetails}
            />
          ))
        )}
      </div>

      {/* Column Footer - Quick Add */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={() => onQuickAdd(stage)}
          className="w-full py-2 px-3 min-h-[44px] flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-dashed border-gray-300 hover:border-purple-300"
        >
          <Plus size={18} />
          <span>Quick Add</span>
        </button>
      </div>
    </div>
  );
};

export default PipelineColumn;
